import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/SVG/Clinic Logo.png';
import { getCurrentUser } from '../../services/userService';
import { User, Clinic } from '../../types';
import { useClinic } from '../ClinicProvider';
import TenantLogo from './TenantLogo';


const containerSx = {
  display: 'flex',
  height: 100,
  borderBottom: '0.1px solid #3f3f3f',
};
const leftSectionSx = {
  backgroundColor: '#fdf4f1',
  padding: 2,
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
};
const dividerSx = { width: 20, backgroundColor: '#e97c5e' };
const rightSectionSx = {
  width: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 2,
  backgroundColor: '#fff',
};
const logoSx: React.CSSProperties = { width: 120, height: 'auto', objectFit: 'contain' };

export const ClinicHeader: React.FC = () => {
  const { t: tGeneral } = useTranslation('general');
  const { ClinicId } = useClinic();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchUser = async () => {
      try {
        const userData = await getCurrentUser();
        if (isMounted) {
          setCurrentUser(userData);
          setError(null);
        }
      } catch {
        setCurrentUser(null);
        setError('Failed to load user');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      isMounted = false;
    };
  }, []);

  const currentClinic = useMemo(() => {
    if (!currentUser?.clinics || !ClinicId) return null;
    return currentUser.clinics.find((c: Clinic) => c.clinic_id === Number(ClinicId)) || null;
  }, [currentUser, ClinicId]);

  const tenantName = useMemo(() => currentUser?.tenant?.name || '', [currentUser]);
  const logoUrl = useMemo(() => {
    const url = currentUser?.tenant?.photo;
    return url && url.trim() !== '' ? url : '';
  }, [currentUser]);


  const clinicName = useMemo(() => currentClinic?.clinic__name || '', [currentClinic]);
  const userEmail = useMemo(() => currentUser?.email || '', [currentUser]);

  if (loading) {
    return (
      <Box sx={containerSx}>
        <Typography variant="body2" sx={{ m: 2 }}>{tGeneral('Loading')}</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={containerSx}>
        <Typography variant="body2" color="error" sx={{ m: 2 }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      {/* Left Section with Background */}
      <Box sx={leftSectionSx}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {tenantName}
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 700 }}>
            {clinicName}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>{tGeneral('email')}:</strong> {userEmail}
          </Typography>
        </Box>
      </Box>
      
      {/* Divider */}
      <Box sx={dividerSx} />

      {/* Right Section with Logo */}
      <Box sx={rightSectionSx}>
        <TenantLogo
          logoUrl={logoUrl}
          fallback={logo}
          alt={tGeneral('clinicLogo')}
          sx={logoSx}
        />
      </Box>
    </Box>
  );
};
